/**
 * Analytics API Service Layer
 *
 * Handles loading analytics data from JSON report files generated by Python analyzers.
 * Provides type-safe access to risk data, technical debt, predictions, and insights.
 *
 * Expected file structure:
 * - /data/analytics/analytics_report.json (served from public/)
 *
 * Note: For development/demo purposes, this API also generates mock data
 * when no analytics report is available.
 */

import type {
  AnalyticsReport,
  RiskData,
  DebtSummaryByCategory,
  DebtItem,
  TimeSeriesDataPoint,
  PredictionSummary,
  AnalyticsInsight,
} from '../types/analytics';
import { logger } from '../helpers/logger';

// ============================================================================
// Raw Report Types (from Python analyzer)
// ============================================================================

interface RawAnalyticsReport {
  risk_data: RawRiskData[];
  debt_summary: {
    total_hours: number;
    by_category: RawDebtCategory[];
    items: RawDebtItem[];
    historical: RawTimeSeriesPoint[];
    targets: RawTimeSeriesPoint[];
  };
  predictions: {
    quality: RawPrediction;
    coverage: RawPrediction;
    issues: RawPrediction;
    debt: RawPrediction;
  };
  insights: RawInsight[];
  metadata: {
    generated_at: string;
    analyzer_version: string;
    data_quality: number;
    files_analyzed: number;
  };
}

interface RawRiskData {
  path: string;
  display_name: string;
  risk_score: number;
  risk_level: string;
  factors: Array<{
    type: string;
    weight: number;
    value: number;
    score: number;
    description?: string;
  }>;
  confidence: number;
  last_updated: string;
  directory?: string;
}

interface RawDebtCategory {
  category: string;
  label: string;
  total_hours: number;
  percentage: number;
  item_count: number;
  color: string;
}

interface RawDebtItem {
  id: string;
  category: string;
  description: string;
  estimated_hours: number;
  priority: string;
  affected_files: string[];
  created_at: string;
}

interface RawTimeSeriesPoint {
  timestamp: string;
  value: number;
  label?: string;
}

interface RawPrediction {
  current: number;
  projected: number;
  confidence: number;
  confidence_level: string;
  timeframe_days: number;
  insight: string;
  goal_value?: number;
  goal_date?: string;
}

interface RawInsight {
  id: string;
  priority: string;
  title: string;
  description: string;
  category: string;
  affected_files: string[];
  impact: {
    min: number;
    max: number;
    unit: string;
    description?: string;
  };
  effort: {
    hours: number;
    difficulty: string;
    skills?: string[];
  };
  tags: string[];
  created_at: string;
  dismissed_at?: string;
}

// ============================================================================
// Transform Functions
// ============================================================================

function transformRiskData(raw: RawRiskData): RiskData {
  return {
    path: raw.path,
    displayName: raw.display_name,
    riskScore: raw.risk_score,
    riskLevel: raw.risk_level as RiskData['riskLevel'],
    factors: raw.factors.map((f) => ({
      type: f.type as RiskData['factors'][0]['type'],
      weight: f.weight,
      value: f.value,
      score: f.score,
      description: f.description,
    })),
    confidence: raw.confidence,
    lastUpdated: raw.last_updated,
    directory: raw.directory,
  };
}

function transformDebtCategory(raw: RawDebtCategory): DebtSummaryByCategory {
  return {
    category: raw.category as DebtSummaryByCategory['category'],
    label: raw.label,
    totalHours: raw.total_hours,
    percentage: raw.percentage,
    itemCount: raw.item_count,
    color: raw.color,
  };
}

function transformDebtItem(raw: RawDebtItem): DebtItem {
  return {
    id: raw.id,
    category: raw.category as DebtItem['category'],
    description: raw.description,
    estimatedHours: raw.estimated_hours,
    priority: raw.priority as DebtItem['priority'],
    affectedFiles: raw.affected_files,
    createdAt: raw.created_at,
  };
}

function transformTimeSeriesPoint(raw: RawTimeSeriesPoint): TimeSeriesDataPoint {
  return {
    timestamp: raw.timestamp,
    value: raw.value,
    label: raw.label,
  };
}

function transformPrediction(raw: RawPrediction): PredictionSummary {
  return {
    current: raw.current,
    projected: raw.projected,
    confidence: raw.confidence,
    confidenceLevel: raw.confidence_level as PredictionSummary['confidenceLevel'],
    timeframeDays: raw.timeframe_days,
    insight: raw.insight,
    goalValue: raw.goal_value,
    goalDate: raw.goal_date,
  };
}

function transformInsight(raw: RawInsight): AnalyticsInsight {
  return {
    id: raw.id,
    priority: raw.priority as AnalyticsInsight['priority'],
    title: raw.title,
    description: raw.description,
    category: raw.category,
    affectedFiles: raw.affected_files,
    impact: {
      min: raw.impact.min,
      max: raw.impact.max,
      unit: raw.impact.unit,
      description: raw.impact.description,
    },
    effort: {
      hours: raw.effort.hours,
      difficulty: raw.effort.difficulty as AnalyticsInsight['effort']['difficulty'],
      skills: raw.effort.skills,
    },
    tags: raw.tags,
    createdAt: raw.created_at,
    dismissedAt: raw.dismissed_at,
  };
}

function transformAnalyticsReport(raw: RawAnalyticsReport): AnalyticsReport {
  return {
    riskData: raw.risk_data.map(transformRiskData),
    debtSummary: {
      totalHours: raw.debt_summary.total_hours,
      byCategory: raw.debt_summary.by_category.map(transformDebtCategory),
      items: raw.debt_summary.items.map(transformDebtItem),
      historical: raw.debt_summary.historical.map(transformTimeSeriesPoint),
      targets: raw.debt_summary.targets.map(transformTimeSeriesPoint),
    },
    predictions: {
      quality: transformPrediction(raw.predictions.quality),
      coverage: transformPrediction(raw.predictions.coverage),
      issues: transformPrediction(raw.predictions.issues),
      debt: transformPrediction(raw.predictions.debt),
    },
    insights: raw.insights.map(transformInsight),
    metadata: {
      generatedAt: raw.metadata.generated_at,
      analyzerVersion: raw.metadata.analyzer_version,
      dataQuality: raw.metadata.data_quality,
      filesAnalyzed: raw.metadata.files_analyzed,
    },
  };
}

// ============================================================================
// Validation Functions
// ============================================================================

function validateAnalyticsReport(data: unknown): data is RawAnalyticsReport {
  if (typeof data !== 'object' || data === null) {
    return false;
  }
  const report = data as Record<string, unknown>;
  return (
    Array.isArray(report.risk_data) &&
    typeof report.debt_summary === 'object' &&
    typeof report.predictions === 'object' &&
    Array.isArray(report.insights) &&
    typeof report.metadata === 'object'
  );
}

// ============================================================================
// Mock Data Generation (for development/demo)
// ============================================================================

function generateMockRiskData(): RiskData[] {
  const modules = [
    { path: 'src/features/dashboard', name: 'Dashboard' },
    { path: 'src/components/charts', name: 'Charts' },
    { path: 'src/utils/validation', name: 'Validation' },
    { path: 'src/api/endpoints', name: 'API' },
    { path: 'src/hooks/data', name: 'Data Hooks' },
    { path: 'src/features/auth', name: 'Auth' },
    { path: 'src/services/storage', name: 'Storage' },
    { path: 'src/types/models', name: 'Models' },
  ];

  return modules.map((module) => {
    const complexity = Math.random() * 100;
    const coverage = 100 - Math.random() * 100;
    const dependencies = Math.random() * 100;
    const age = Math.random() * 100;
    const churn = Math.random() * 100;

    const riskScore =
      complexity * 0.25 + coverage * 0.3 + dependencies * 0.2 + age * 0.1 + churn * 0.15;

    const getRiskLevel = (score: number): RiskData['riskLevel'] => {
      if (score >= 80) return 'critical';
      if (score >= 60) return 'high';
      if (score >= 40) return 'medium';
      if (score >= 20) return 'low';
      return 'minimal';
    };

    return {
      path: module.path,
      displayName: module.name,
      riskScore: Math.round(riskScore * 10) / 10,
      riskLevel: getRiskLevel(riskScore),
      factors: [
        { type: 'complexity' as const, weight: 0.25, value: complexity, score: complexity },
        { type: 'coverage' as const, weight: 0.3, value: coverage, score: coverage },
        { type: 'dependencies' as const, weight: 0.2, value: dependencies, score: dependencies },
        { type: 'age' as const, weight: 0.1, value: age, score: age },
        { type: 'churn' as const, weight: 0.15, value: churn, score: churn },
      ],
      confidence: 75 + Math.random() * 20,
      lastUpdated: new Date().toISOString(),
      directory: module.path.split('/').slice(0, -1).join('/'),
    };
  });
}

function generateMockDebtData(): AnalyticsReport['debtSummary'] {
  const categories: DebtSummaryByCategory[] = [
    { category: 'complexity', label: 'Complexity', totalHours: 35, percentage: 35, itemCount: 12, color: '#e53935' },
    { category: 'coverage', label: 'Coverage', totalHours: 28, percentage: 28, itemCount: 8, color: '#ff9800' },
    { category: 'documentation', label: 'Documentation', totalHours: 15, percentage: 15, itemCount: 15, color: '#ffc107' },
    { category: 'dependencies', label: 'Dependencies', totalHours: 12, percentage: 12, itemCount: 5, color: '#4caf50' },
    { category: 'security', label: 'Security', totalHours: 7, percentage: 7, itemCount: 3, color: '#2196f3' },
    { category: 'performance', label: 'Performance', totalHours: 3, percentage: 3, itemCount: 2, color: '#9c27b0' },
  ];

  const items: DebtItem[] = [
    {
      id: 'debt-1',
      category: 'complexity',
      description: 'Refactor Dashboard component - too many responsibilities',
      estimatedHours: 8,
      priority: 'high',
      affectedFiles: ['src/features/dashboard/Dashboard.tsx'],
      createdAt: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(),
    },
    {
      id: 'debt-2',
      category: 'coverage',
      description: 'Add tests for API endpoints',
      estimatedHours: 6,
      priority: 'high',
      affectedFiles: ['src/api/endpoints.ts'],
      createdAt: new Date(Date.now() - 14 * 24 * 60 * 60 * 1000).toISOString(),
    },
    {
      id: 'debt-3',
      category: 'documentation',
      description: 'Document public API functions',
      estimatedHours: 4,
      priority: 'medium',
      affectedFiles: ['src/api/index.ts', 'src/utils/index.ts'],
      createdAt: new Date(Date.now() - 21 * 24 * 60 * 60 * 1000).toISOString(),
    },
  ];

  // Generate historical data (last 90 days)
  const historical: TimeSeriesDataPoint[] = [];
  const targets: TimeSeriesDataPoint[] = [];
  const startDebt = 150;
  const currentDebt = 100;
  const targetDebt = 50;

  for (let i = 90; i >= 0; i -= 7) {
    const date = new Date(Date.now() - i * 24 * 60 * 60 * 1000);
    const progress = (90 - i) / 90;
    const actualValue = startDebt - (startDebt - currentDebt) * progress + (Math.random() - 0.5) * 10;
    const targetValue = startDebt - (startDebt - targetDebt) * progress;

    historical.push({
      timestamp: date.toISOString(),
      value: Math.max(0, Math.round(actualValue)),
    });
    targets.push({
      timestamp: date.toISOString(),
      value: Math.max(0, Math.round(targetValue)),
    });
  }

  return {
    totalHours: 100,
    byCategory: categories,
    items,
    historical,
    targets,
  };
}

function generateMockPredictions(): AnalyticsReport['predictions'] {
  return {
    quality: {
      current: 82.5,
      projected: 88.2,
      confidence: 82,
      confidenceLevel: 'high',
      timeframeDays: 90,
      insight: 'If current pace continues, quality score will reach your target of 90% by June.',
      goalValue: 90,
      goalDate: new Date(Date.now() + 120 * 24 * 60 * 60 * 1000).toISOString(),
    },
    coverage: {
      current: 72.5,
      projected: 78.0,
      confidence: 75,
      confidenceLevel: 'medium',
      timeframeDays: 90,
      insight: 'Coverage is improving steadily. Consider focusing on untested utility functions.',
    },
    issues: {
      current: 23,
      projected: 15,
      confidence: 68,
      confidenceLevel: 'medium',
      timeframeDays: 90,
      insight: 'Issue count is trending downward. Critical issues should be prioritized.',
    },
    debt: {
      current: 100,
      projected: 75,
      confidence: 72,
      confidenceLevel: 'medium',
      timeframeDays: 90,
      insight: 'Technical debt is being reduced at a sustainable pace.',
      goalValue: 50,
      goalDate: new Date(Date.now() + 180 * 24 * 60 * 60 * 1000).toISOString(),
    },
  };
}

function generateMockInsights(): AnalyticsInsight[] {
  return [
    {
      id: 'insight-1',
      priority: 'high',
      title: 'Improve Type Safety',
      description: 'Several files are missing TypeScript strict mode compliance. Adding strict types will improve code quality and catch bugs earlier.',
      category: 'code-quality',
      affectedFiles: [
        'src/utils/validation.ts',
        'src/api/endpoints.ts',
        'src/hooks/useData.ts',
        'src/services/storage.ts',
        'src/types/models.ts',
      ],
      impact: { min: 5, max: 10, unit: '%', description: 'Quality score improvement' },
      effort: { hours: 4, difficulty: 'moderate', skills: ['TypeScript'] },
      tags: ['typescript', 'type-safety', 'quality'],
      createdAt: new Date().toISOString(),
    },
    {
      id: 'insight-2',
      priority: 'medium',
      title: 'Reduce Circular Dependencies',
      description: 'Found 3 circular dependency chains that could lead to initialization issues and make the codebase harder to maintain.',
      category: 'architecture',
      affectedFiles: [
        'src/features/dashboard/index.ts',
        'src/components/charts/index.ts',
        'src/utils/index.ts',
      ],
      impact: { min: 3, max: 5, unit: '%', description: 'Maintainability improvement' },
      effort: { hours: 8, difficulty: 'complex', skills: ['Architecture', 'Refactoring'] },
      tags: ['dependencies', 'architecture', 'maintainability'],
      createdAt: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
    },
    {
      id: 'insight-3',
      priority: 'low',
      title: 'Add Missing Documentation',
      description: 'Public API functions are missing JSDoc comments, making it harder for other developers to understand the codebase.',
      category: 'documentation',
      affectedFiles: [
        'src/api/index.ts',
        'src/utils/helpers.ts',
      ],
      impact: { min: 1, max: 2, unit: '%', description: 'Developer experience improvement' },
      effort: { hours: 2, difficulty: 'easy', skills: ['Documentation'] },
      tags: ['documentation', 'jsdoc', 'developer-experience'],
      createdAt: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(),
    },
  ];
}

function generateMockAnalyticsReport(): AnalyticsReport {
  return {
    riskData: generateMockRiskData(),
    debtSummary: generateMockDebtData(),
    predictions: generateMockPredictions(),
    insights: generateMockInsights(),
    metadata: {
      generatedAt: new Date().toISOString(),
      analyzerVersion: '1.0.0-mock',
      dataQuality: 85,
      filesAnalyzed: 156,
    },
  };
}

// ============================================================================
// Analytics API
// ============================================================================

/**
 * Analytics API service for loading analytics data
 */
export const analyticsApi = {
  /**
   * Load analytics report from file system
   *
   * @param outputsPath - Base path to data directory (e.g., '/data')
   * @returns Analytics report or mock data if file doesn't exist
   */
  async loadAnalyticsReport(outputsPath: string): Promise<AnalyticsReport> {
    const path = `${outputsPath}/analytics/analytics_report.json`;

    try {
      const response = await fetch(path);
      if (!response.ok) {
        if (response.status === 404) {
          logger.warn('analyticsApi', 'Analytics report not found, using mock data', { path });
          return generateMockAnalyticsReport();
        }
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const text = await response.text();
      const rawData = JSON.parse(text) as unknown;

      if (!validateAnalyticsReport(rawData)) {
        logger.warn('analyticsApi', 'Analytics report has invalid structure, using mock data');
        return generateMockAnalyticsReport();
      }

      const data = transformAnalyticsReport(rawData);
      logger.info(
        'analyticsApi',
        `Loaded analytics report: ${data.riskData.length} risk items, ${data.insights.length} insights`
      );
      return data;
    } catch (error) {
      logger.warn('analyticsApi', 'Error loading analytics report', { error });
      return generateMockAnalyticsReport();
    }
  },

  /**
   * Load risk data only
   */
  async loadRiskData(outputsPath: string): Promise<RiskData[]> {
    const report = await this.loadAnalyticsReport(outputsPath);
    return report.riskData;
  },

  /**
   * Load debt summary only
   */
  async loadDebtSummary(outputsPath: string): Promise<AnalyticsReport['debtSummary']> {
    const report = await this.loadAnalyticsReport(outputsPath);
    return report.debtSummary;
  },

  /**
   * Load predictions only
   */
  async loadPredictions(outputsPath: string): Promise<AnalyticsReport['predictions']> {
    const report = await this.loadAnalyticsReport(outputsPath);
    return report.predictions;
  },

  /**
   * Load insights only
   */
  async loadInsights(outputsPath: string): Promise<AnalyticsInsight[]> {
    const report = await this.loadAnalyticsReport(outputsPath);
    return report.insights;
  },

  /**
   * Dismiss an insight (mock implementation)
   */
  async dismissInsight(insightId: string): Promise<{ success: boolean }> {
    // In a real implementation, this would call a backend API
    logger.info('analyticsApi', `Dismissing insight: ${insightId}`);
    return { success: true };
  },
};
